import React, { useEffect, useState } from "react";
import { useSearchParams, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import RecipeCard from "@/components/RecipeCard";
import Seo from "@/components/Seo";
import { Filter } from "lucide-react";

const CATEGORIES = [
  { v: "", label: "Toutes" },
  { v: "entree", label: "Entrées" },
  { v: "plat", label: "Plats" },
  { v: "dessert", label: "Desserts" },
  { v: "petit-dejeuner", label: "Petit-déjeuner" },
  { v: "accompagnement", label: "Accompagnements" },
];

const GI_LEVELS = [
  { v: "", label: "Tous IG" },
  { v: "bas", label: "IG Bas (<55)" },
  { v: "modere", label: "IG Modéré" },
];

export default function RecipesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { categorySlug } = useParams();
  const initialCategory = categorySlug || searchParams.get("category") || "";
  const initialQ = searchParams.get("q") || "";
  const initialGi = searchParams.get("gi_level") || "";
  const initialCountry = searchParams.get("country") || "";

  const [category, setCategory] = useState(initialCategory);
  const [gi, setGi] = useState(initialGi);
  const [country, setCountry] = useState(initialCountry);
  const [q, setQ] = useState(initialQ);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    api.get("/recipes/origins").then((r) => setCountries(r.data.countries || []));
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (category) params.set("category", category);
        if (gi) params.set("gi_level", gi);
        if (country) params.set("country", country);
        if (q) params.set("q", q);
        params.set("limit", "48");
        const res = await api.get(`/recipes?${params}`);
        setItems(res.data.items || []);
        setTotal(res.data.total || 0);
      } finally {
        setLoading(false);
      }
    };
    load();
    const newParams = {};
    if (category && !categorySlug) newParams.category = category;
    if (gi) newParams.gi_level = gi;
    if (country) newParams.country = country;
    if (q) newParams.q = q;
    setSearchParams(newParams);
  }, [category, gi, country, q, categorySlug, setSearchParams]);

  const pageTitle = categorySlug
    ? `Recettes — ${CATEGORIES.find((c) => c.v === categorySlug)?.label || categorySlug}`
    : "Toutes les recettes";

  return (
    <>
      <Seo
        title={pageTitle}
        description="Catalogue de recettes traditionnelles françaises adaptées aux diabétiques. Filtrez par catégorie, index glycémique et difficulté."
        keywords={["recettes diabétiques", "IG bas", "cuisine française"]}
        path={categorySlug ? `/recettes?categorie=${categorySlug}` : "/recettes"}
      />
      <section className="container-x pt-16 pb-8" data-testid="recipes-header">
        <p className="overline mb-3">Catalogue · {total} recettes</p>
        <h1 className="font-serif text-5xl md:text-6xl tracking-tight">{pageTitle}</h1>
        <p className="mt-4 text-[hsl(var(--text-secondary))] max-w-2xl">
          {categorySlug
            ? "Sélection de recettes traditionnelles adaptées pour les diabétiques."
            : "Explorez toutes nos recettes : sélectionnez une catégorie, un niveau d'index glycémique, ou cherchez par mot-clé."}
        </p>
      </section>

      <section className="container-x pb-6 sticky top-16 md:top-20 z-30 bg-[hsl(var(--bg-main))]/95 backdrop-blur-sm" data-testid="recipes-filters">
        <div className="border-y border-[hsl(var(--border-light))] py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-2 overflow-x-auto">
            <Filter className="w-4 h-4 text-[hsl(var(--text-muted))]" strokeWidth={1.5} />
            {CATEGORIES.map((c) => (
              <button
                key={c.v || "all"}
                onClick={() => setCategory(c.v)}
                data-testid={`filter-category-${c.v || "all"}`}
                className={`text-xs uppercase tracking-wider px-3 py-1.5 rounded-sm transition-colors whitespace-nowrap ${
                  category === c.v
                    ? "bg-[hsl(var(--text-primary))] text-[hsl(var(--bg-main))]"
                    : "text-[hsl(var(--text-secondary))] hover:text-[hsl(var(--text-primary))]"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <select
              data-testid="filter-country-select"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className="text-sm bg-white border border-[hsl(var(--border-light))] rounded-sm px-3 py-1.5 focus:outline-none focus:border-[hsl(var(--brand-primary))]"
            >
              <option value="">Tous pays</option>
              {countries.map((c) => (
                <option key={c.name} value={c.name}>{c.name} ({c.count})</option>
              ))}
            </select>
            <select
              data-testid="filter-gi-select"
              value={gi}
              onChange={(e) => setGi(e.target.value)}
              className="text-sm bg-white border border-[hsl(var(--border-light))] rounded-sm px-3 py-1.5 focus:outline-none focus:border-[hsl(var(--brand-primary))]"
            >
              {GI_LEVELS.map((g) => (
                <option key={g.v} value={g.v}>{g.label}</option>
              ))}
            </select>
            <input
              data-testid="recipes-search-input"
              type="search"
              placeholder="Rechercher…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="text-sm bg-white border border-[hsl(var(--border-light))] rounded-sm px-3 py-1.5 w-48 focus:outline-none focus:border-[hsl(var(--brand-primary))]"
            />
          </div>
        </div>
      </section>

      <section className="container-x py-10 pb-24">
        {loading ? (
          <div className="text-[hsl(var(--text-muted))] py-12">Chargement…</div>
        ) : items.length === 0 ? (
          <div className="border border-dashed border-[hsl(var(--border-default))] rounded-sm p-12 text-center" data-testid="no-recipes-message">
            <p className="font-serif text-2xl mb-2">Aucune recette ne correspond.</p>
            <p className="text-sm text-[hsl(var(--text-muted))]">Essayez d'élargir vos filtres ou revenez plus tard — de nouvelles recettes sont publiées chaque jour.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12">
            {items.map((r) => (
              <RecipeCard key={r.id} recipe={r} />
            ))}
          </div>
        )}
      </section>
    </>
  );
}
